import React, { useState, useEffect } from "react";
import styles from "./DealerShipDetails.module.scss";
import { Button, Grid, TextField } from "@mui/material";
import MultiToggleWidget from "../../../shared/toggles/multiToggleWidget/MultiToggleWidget";
import EditIcon from "../../../shared/Edit/EditIcon";
import ClearIcon from "@mui/icons-material/Clear";
import { withTranslation } from "react-i18next";
import HasPermissionTo from "../../../common/HasPermissionTo";
import DealerDetailsClient from "../../../shared/clients/DealerDetailsClient";

const DealerEmployeeTable = (props) => {
  const [employees, setEmployees] = useState([]);
  const [addNewRowActive, setAddNewRowActive] = useState(false);
  const [newEmployee, setNewEmployee] = useState(refreshNewEmployee());
  const [employeesToEdit, setEmployeesToEdit] = useState(new Map());
  const [employeeLimitReached, setEmployeeLimitReached] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const currentDealer = props.dealers;

  useEffect(() => {
    getDealerEmployees();
  }, []);

  useEffect(() => {
    if (props.renderAgain) {
      getDealerEmployees();
    }
  }, [props.renderAgain]);

  function refreshNewEmployee() {
    return {
      id: "",
      dealerId: props.dealers.completeDealerId,
      fullName: "",
      emailAddress: "",
      receiveEmailsForNotifications: true,
      receiveEmailsForPendingActions: true,
    };
  }

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return props.t("Error.requiredField");
    }
    if (!emailRegex.test(email)) {
      return props.t("Error.invalidInput");
    }
    return "";
  };

  const validateRequired = (value) => {
    if (!value) {
      return props.t("Error.requiredField");
    }
    return "";
  };

  const onSelectEmailNotification = (employee, event) => {
    const selectedStatus = event.target.checked;

    DealerDetailsClient.updateDealerEmployeeEmailNotifications(
      props.user,
      employee,
      selectedStatus
    ).then(() => {
      const copy = [...employees];
      const index = employees.findIndex((emp) => emp.id === employee.id);
      const employeeToChange = { ...copy[index] };
      employeeToChange.receiveEmailsForNotifications = selectedStatus;
      copy.splice(index, 1, employeeToChange);
      setEmployees(copy);
      resetRow(employee.id);
    });
  };

  const onSelectPendingActionNotification = (employee, event) => {
    const selectedStatus = event.target.checked;

    DealerDetailsClient.updateDealerEmployeePendingActionsEmails(
      props.user,
      employee,
      selectedStatus
    ).then(() => {
      const copy = [...employees];
      const index = employees.findIndex((emp) => emp.id === employee.id);
      const employeeToChange = { ...copy[index] };
      employeeToChange.receiveEmailsForPendingActions = selectedStatus;
      copy.splice(index, 1, employeeToChange);
      setEmployees(copy);
      resetRow(employee.id);
    });
  };

  const getDealerEmployees = () => {
    DealerDetailsClient.getDealerEmployees(
      props.user,
      currentDealer.completeDealerId
    )
      .then((employeesData) => {
        setEmployees(employeesData);
        setEmployeeLimitReached(employeesData.length > 9);
        props.disableLiveUpdateEmployees();
      })
      .catch(() => {});
  };

  const refreshEmployees = () => {
    props.enableLiveUpdateEmployees();
  };

  const editExistingEmployee = (event) => {
    const editEmployee = employees.find(
      (employee) => employee.id === parseInt(event.currentTarget.value)
    );
    const copy = new Map(employeesToEdit);
    copy.set(editEmployee.id, editEmployee);
    setEmployeesToEdit(copy);
  };

  const deleteExistingEmployee = (event) => {
    const employeeId = parseInt(event.currentTarget.value);
    DealerDetailsClient.deleteDealerEmployee(props.user, employeeId).then(
      () => {
        resetRow(employeeId);
        const copy = employees.filter((emp) => emp.id !== employeeId);
        setEmployees(copy);
        setEmployeeLimitReached(copy.length > 9);
      }
    );
  };

  const editEmployeeNameOnChange = (employeeId, event) => {
    const modifiedName = event.target.value;
    const copy = new Map(employeesToEdit);
    const newEmployee = {
      ...copy.get(employeeId),
      fullName: modifiedName,
    };
    setEmployeesToEdit(copy.set(employeeId, newEmployee));

    setValidationErrors((prev) => ({
      ...prev,
      [`name_${employeeId}`]: "",
    }));
  };

  const editEmployeeEmailOnChange = (employeeId, event) => {
    const modifiedEmail = event.target.value;
    const copy = new Map(employeesToEdit);
    const newEmployee = {
      ...copy.get(employeeId),
      emailAddress: modifiedEmail,
    };
    setEmployeesToEdit(copy.set(employeeId, newEmployee));

    setValidationErrors((prev) => ({
      ...prev,
      [`email_${employeeId}`]: "",
    }));
  };

  const updateEmployee = (employee) => {
    const nameError = validateRequired(employee.fullName);
    const emailError = validateEmail(employee.emailAddress);

    if (nameError || emailError) {
      setValidationErrors({
        [`name_${employee.id}`]: nameError,
        [`email_${employee.id}`]: emailError,
      });
      return;
    }

    DealerDetailsClient.updateDealerEmployee(props.user, employee).then(
      (updateEmployee) => {
        const copy = [...employees];
        const index = employees.findIndex(
          (emp) => emp.id === updateEmployee.id
        );
        copy.splice(index, 1, updateEmployee);
        setEmployees(copy);
        resetRow(updateEmployee.id);
        refreshEmployees();
        setValidationErrors({});
      }
    );
  };

  const cancelEdit = (event) => {
    resetRow(event.currentTarget.value);
    setValidationErrors({});
  };

  const resetRow = (employeeId) => {
    const empId = parseInt(employeeId);
    if (employeesToEdit.has(empId)) {
      const copy = new Map(employeesToEdit);
      copy.delete(empId);
      setEmployeesToEdit(copy);
    }
  };

  const addNewEmployee = (e) => {
    e.preventDefault();

    const nameError = validateRequired(newEmployee.fullName);
    const emailError = validateEmail(newEmployee.emailAddress);

    if (nameError || emailError) {
      setValidationErrors({
        newName: nameError,
        newEmail: emailError,
      });
      return;
    }

    DealerDetailsClient.addEmployee(
      props.user,
      newEmployee,
      currentDealer.completeDealerId
    ).then((newEmp) => {
      setEmployees([...employees, newEmp]);
      setAddNewRowActive(false);
      setNewEmployee(refreshNewEmployee());
      setValidationErrors({});
      refreshEmployees();
    });
  };

  const cancelNewEmployee = () => {
    setAddNewRowActive(false);
    setNewEmployee(refreshNewEmployee());
    setValidationErrors({});
  };

  const showNewEmployeeRow = () => {
    setAddNewRowActive(true);
  };

  const newEmployeeEmailOnChange = (event) => {
    setNewEmployee({
      ...newEmployee,
      emailAddress: event.target.value,
    });
    setValidationErrors((prev) => ({ ...prev, newEmail: "" }));
  };

  const newEmployeeNameOnChange = (event) => {
    setNewEmployee({
      ...newEmployee,
      fullName: event.target.value,
    });
    setValidationErrors((prev) => ({ ...prev, newName: "" }));
  };

  return (
    <>
      <div className={styles.employeeEmailHeader}>
        <div className={styles.sectionHeaderEmployees}>
          {props.t("DealerShipDetails.employeeHeader")}
        </div>
        {employees.length > 0 && (
          <>
            <Grid container className={styles.subHeader}>
              <Grid item md={3} className={styles.employeeInfoHeader}>
                {props.t("DealerShipDetails.employees")}
              </Grid>
              <Grid item md={3} className={styles.employeeInfoHeader}>
                {props.t("DealerShipDetails.employeesEmail")}
              </Grid>
              <Grid item md={2} className={styles.notifications}>
                <div>{props.t("DealerShipDetails.employeesNotifications")}</div>
              </Grid>

              <Grid item md={2} className={styles.notifications}>
                <HasPermissionTo
                  perform={["receivePendingActionColumn"]}
                  permissions={props.user.permissions.rolePermissions}
                  render={() => (
                    <div>
                      {props.t("DealerShipDetails.employeesPendingActions")}
                    </div>
                  )}
                />
              </Grid>
              <Grid item md={2} className={styles.employeeInfoHeader}>
                &nbsp;
              </Grid>
            </Grid>

            {employees.map((employee, index) => (
              <div key={`employee_${index}`}>
                {employeesToEdit.has(employee.id) ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      updateEmployee(employeesToEdit.get(employee.id));
                    }}
                  >
                    <Grid
                      container
                      className={`${styles.tableBody} ${styles.employeeInfo}`}
                    >
                      <Grid item md={3} className={styles.employeeName}>
                        <TextField
                          name={`fullName_${employee.id}`}
                          style={{ width: "70%" }}
                          value={employeesToEdit.get(employee.id).fullName}
                          variant="outlined"
                          onChange={(e) =>
                            editEmployeeNameOnChange(employee.id, e)
                          }
                          error={!!validationErrors[`name_${employee.id}`]}
                          helperText={validationErrors[`name_${employee.id}`]}
                        />
                      </Grid>
                      <Grid item md={3} className={styles.employeeEmail}>
                        <TextField
                          name={`email_${employee.id}`}
                          style={{ width: "70%" }}
                          value={employeesToEdit.get(employee.id).emailAddress}
                          variant="outlined"
                          onChange={(e) =>
                            editEmployeeEmailOnChange(employee.id, e)
                          }
                          error={!!validationErrors[`email_${employee.id}`]}
                          helperText={validationErrors[`email_${employee.id}`]}
                        />
                      </Grid>
                      <Grid item className={styles.employeeSwitches} md={2}>
                        {employee.receiveEmailsForNotifications
                          ? props.t("DealerShipDetails.yes")
                          : props.t("DealerShipDetails.no")}
                      </Grid>
                      <HasPermissionTo
                        perform={["receivePendingActionColumn"]}
                        permissions={props.user.permissions.rolePermissions}
                        render={() => (
                          <Grid item className={styles.employeeSwitches} md={2}>
                            {employee.receiveEmailsForPendingActions
                              ? props.t("DealerShipDetails.yes")
                              : props.t("DealerShipDetails.no")}
                          </Grid>
                        )}
                        noRender={() => <Grid item md={2} />}
                      />
                      <Grid item md={2} className={styles.editDiv}>
                        <Button
                          id={`updateEmployee_${employee.id}`}
                          data-testid={`updateEmployee_${employee.id}`}
                          value={employee.id}
                          type="submit"
                        >
                          <div className={styles.confirm}>OK</div>
                        </Button>
                        <Button
                          id={`cancelEdit_${employee.id}`}
                          data-testid={`cancelEdit_${employee.id}`}
                          value={employee.id}
                          onClick={cancelEdit}
                        >
                          <ClearIcon />
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                ) : (
                  <Grid container className={styles.employeeInfo} key={index}>
                    <Grid
                      item
                      md={3}
                      id={`name_${index}`}
                      className={styles.employeeName}
                    >
                      {employee.fullName}
                    </Grid>
                    <Grid
                      item
                      md={3}
                      id={`email_${index}`}
                      className={styles.employeeEmail}
                    >
                      {employee.emailAddress}
                    </Grid>

                    {!props.isMainViewingSDLR(props.dealers) &&
                    !props.user.isDealerUsedCarsSalesManager ? (
                      <>
                        <Grid item md={2} className={styles.switches}>
                          <MultiToggleWidget
                            settings={{
                              acceptedText: props.t("DealerShipDetails.yes"),
                              rejectedText: props.t("DealerShipDetails.no"),
                              suppClass: `${styles.toggleWrapper}`,
                              toggleLabel: `${styles.activationToggle}`,
                            }}
                            fromDealerTable={true}
                            isMainViewingSDLR=""
                            toggleName={`emailNot_${employee.id}`}
                            currentStatus={
                              employee.receiveEmailsForNotifications
                                ? "yes"
                                : "no"
                            }
                            onSelect={(e) =>
                              onSelectEmailNotification(employee, e)
                            }
                          />
                        </Grid>
                        <HasPermissionTo
                          perform={["receivePendingActionColumn"]}
                          permissions={props.user.permissions.rolePermissions}
                          render={() => (
                            <Grid item md={2} className={styles.switches}>
                              <MultiToggleWidget
                                settings={{
                                  acceptedText: props.t(
                                    "DealerShipDetails.yes"
                                  ),
                                  rejectedText: props.t("DealerShipDetails.no"),
                                  suppClass: `${styles.toggleWrapper}`,
                                  toggleLabel: `${styles.activationToggle}`,
                                }}
                                fromDealerTable={true}
                                toggleName={`pendingAcNot_${employee.id}`}
                                currentStatus={
                                  employee.receiveEmailsForPendingActions
                                    ? "yes"
                                    : "no"
                                }
                                onSelect={(e) =>
                                  onSelectPendingActionNotification(employee, e)
                                }
                              />
                            </Grid>
                          )}
                          noRender={() => <Grid item md={2}></Grid>}
                        />
                      </>
                    ) : (
                      <>
                        <Grid
                          item
                          md={2}
                          id={`toggle1_${index}`}
                          className={`${styles.employeeSwitches} switch-3 switch-candy`}
                        >
                          {employee.receiveEmailsForNotifications
                            ? props.t("DealerShipDetails.yes")
                            : props.t("DealerShipDetails.no")}
                        </Grid>
                        <Grid
                          item
                          md={2}
                          id={`toggle2_${index}`}
                          className={`${styles.employeeSwitches} switch-3 switch-candy`}
                        >
                          <HasPermissionTo
                            perform={["receivePendingActionColumn"]}
                            permissions={props.user.permissions.rolePermissions}
                            render={() => (
                              <div>
                                {employee.receiveEmailsForPendingActions
                                  ? props.t("DealerShipDetails.yes")
                                  : props.t("DealerShipDetails.no")}
                              </div>
                            )}
                          />
                        </Grid>
                      </>
                    )}

                    <HasPermissionTo
                      perform={["dealershipDetails:write"]}
                      permissions={props.user.permissions.rolePermissions}
                      condition={!props.isMainViewingSDLR(props.dealers)}
                      render={() => (
                        <Grid item md={2} className={styles.editDiv}>
                          <Button
                            id={`editEmployee_${employee.id}`}
                            data-testid={`editEmployee_${employee.id}`}
                            value={employee.id}
                            onClick={editExistingEmployee}
                          >
                            <EditIcon />
                          </Button>
                          <Button
                            id={`deleteEmployee_${employee.id}`}
                            data-testid={`deleteEmployee_${employee.id}`}
                            value={employee.id}
                            onClick={deleteExistingEmployee}
                          >
                            <ClearIcon />
                          </Button>
                        </Grid>
                      )}
                    />
                  </Grid>
                )}
              </div>
            ))}
          </>
        )}
        {addNewRowActive && (
          <form onSubmit={addNewEmployee}>
            <Grid className={styles.newEmployeeContainer} container>
              <Grid item md={3}>
                <TextField
                  style={{ width: "70%" }}
                  name={`inputName_${currentDealer.regionalId}`}
                  value={newEmployee.fullName}
                  variant="outlined"
                  placeholder={props.t("DealerShipDetails.fullName")}
                  onChange={newEmployeeNameOnChange}
                  error={!!validationErrors.newName}
                  helperText={validationErrors.newName}
                />
              </Grid>
              <Grid item md={3}>
                <TextField
                  style={{ width: "70%" }}
                  name={`inputEmail_${currentDealer.regionalId}`}
                  value={newEmployee.emailAddress}
                  variant="outlined"
                  placeholder={props.t("DealerShipDetails.emailAddress")}
                  onChange={newEmployeeEmailOnChange}
                  error={!!validationErrors.newEmail}
                  helperText={validationErrors.newEmail}
                />
              </Grid>
              <Grid item md={4} />
              <Grid className={styles.actionDiv} item md={2}>
                <Button
                  id={`submit_${currentDealer.regionalId}`}
                  data-testid={`submit_${currentDealer.regionalId}`}
                  type="submit"
                >
                  <div className={styles.confirm}>OK</div>
                </Button>
                <Button
                  id={`cancelAdd_${currentDealer.regionalId}`}
                  data-testid={`cancelAdd_${currentDealer.regionalId}`}
                  onClick={cancelNewEmployee}
                >
                  <ClearIcon />
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </div>
      <HasPermissionTo
        perform={["dealershipDetails:write"]}
        permissions={props.user.permissions.rolePermissions}
        condition={!props.isMainViewingSDLR(props.dealers)}
        render={() => (
          <div
            className={`pagePaddingLeft pagePaddingRight ${styles.editButton}`}
          >
            <Button
              data-testid={`addEmployee_${currentDealer.regionalId}`}
              disabled={addNewRowActive || employeeLimitReached}
              onClick={showNewEmployeeRow}
            >
              {props.t("DealerShipDetails.addButton")}
            </Button>
            {employeeLimitReached && (
              <div className={styles.maximumEmployee}>
                {props.t("DealerShipDetails.maxEmployee")}
              </div>
            )}
          </div>
        )}
      />
    </>
  );
};

export default withTranslation("emp")(DealerEmployeeTable);
